import { cx } from '@linaria/atomic';
import { theme } from '@frontend/theme';
import { useMultiStepContext } from '../../multi-step.context';
import {
  getStepperColors,
  StepperColorProps,
  stepProgress,
  stepperIcon,
  stepperIconWrapper,
  stepperItem,
  stepperItemActive,
  stepperItemClickable,
  stepperItemNotClickable,
  stepperLabel,
  stepperLabelText,
} from '../../multi-step.styles';
import { MultiStepDisplay, StepData } from '../../multi-step.types';

const PROGRESS_SCALE = 0.6;

export type MultiStepStepperItemProps = {
  className?: string;
  step: StepData;
  index: number;
  display?: MultiStepDisplay;
  onClick?: () => void;
  clickDisabled?: boolean;
};
export function MultiStepStepperItem({
  className,
  onClick,
  clickDisabled,
  step,
  index, // parent index
  display,
}: MultiStepStepperItemProps) {
  const {
    activeParentStepIndex,
    parentSteps,
    setActiveStep,
    isActiveParentStep,
    isValidParentStep,
    isCompleteParentStep,
    getParentStepPercentProgress,
  } = useMultiStepContext();

  const prevStep = parentSteps[index - 1] || null;
  const isClickable = !clickDisabled && (step.isComplete || !!prevStep?.isComplete);

  const styleProps: StepperColorProps = {
    hasPrev: index < activeParentStepIndex,
    isNext: index === activeParentStepIndex + 1,
    active: isActiveParentStep(step.id),
    complete: isCompleteParentStep(step.id),
    valid: isValidParentStep(step.id),
    percentComplete: getParentStepPercentProgress(step.id),
    index,
    maxIndex: parentSteps.length - 1,
    isClickable,
    clickDisabled,
  };

  const colors = getStepperColors(styleProps);
  const iconSize = styleProps.active ? 32 : 24;
  const showProgress = index < styleProps.maxIndex;
  const progressOffset = `${iconSize / 2}px`;

  const itemStyle = { '--stepper-item-color': colors.color } as React.CSSProperties;

  const iconStyle = {
    '--stepper-icon-size': `${iconSize}px`,
    '--stepper-icon-border': colors.iconBorder,
    '--stepper-icon-bg': colors.iconBackground,
    '--stepper-icon-color': colors.iconColor,
  } as React.CSSProperties;

  const bgProgressStyle = showProgress
    ? ({
        '--step-progress-height': `${100 * PROGRESS_SCALE}%`,
        '--step-progress-offset': progressOffset,
        '--step-progress-bg': theme.colors.neutral40,
      } as React.CSSProperties)
    : undefined;

  const primaryProgressStyle = showProgress
    ? ({
        '--step-progress-height': `${styleProps.percentComplete * PROGRESS_SCALE}%`,
        '--step-progress-offset': progressOffset,
        '--step-progress-bg': colors.iconBorder,
      } as React.CSSProperties)
    : undefined;

  const clickClass = !clickDisabled ? (isClickable ? stepperItemClickable : stepperItemNotClickable) : undefined;

  const handleStepClick = () => {
    if (!isClickable) {
      return;
    }
    if (typeof onClick === 'function') {
      onClick();
    }

    /**
     * Default functionality
     */

    setActiveStep(step.id);
  };

  return (
    <div
      className={cx(stepperItem, styleProps.active && stepperItemActive, clickClass, 'stepper__item', className)}
      style={itemStyle}
      onClick={handleStepClick}
    >
      <div className={cx('stepper__icon-wrapper', stepperIconWrapper)}>
        <div
          className={cx('stepper__progress', 'stepper__progress--bg', showProgress && stepProgress)}
          style={bgProgressStyle}
        ></div>
        <div
          className={cx('stepper__progress', 'stepper__progress--primary', showProgress && stepProgress)}
          style={primaryProgressStyle}
        ></div>
        <div className={cx('stepper__icon', stepperIcon)} style={iconStyle}>
          <div className='stepper__icon-text'>{index + 1}</div>
        </div>
      </div>
      {display === 'full' && (
        <div className={cx('stepper__label', stepperLabel)}>
          <div className={cx('stepper__label-text', stepperLabelText)}>{step.label}</div>
        </div>
      )}
    </div>
  );
}
